import { APP } from '../constants/app.constants';
import { parseCssColor, pickContrastingForeground } from '../utils/contrast.util';

/** Tenant branding — mirrors `branding_json` in `api-service` (`BrandingValidator`). */

export type BrandingStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE';
export type BrandingTheme = 'light' | 'dark';
export type BrandingSummaryState = 'DEFAULT' | 'DRAFT' | 'PUBLISHED';

export interface ThemeColors {
	primary?: string;
	accent?: string;
	accentHover?: string;
	sidebar?: string;
	sidebarBorder?: string;
	sidebarActive?: string;
}

export interface BrandingConfig {
	schemaVersion: 1;
	identity?: { appName?: string; appTitle?: string; brandName?: string; assistantName?: string };
	logo?: {
		wordmark?: { light?: string; dark?: string };
		mark?: { light?: string; dark?: string };
		favicon?: string;
	};
	colors?: { light?: ThemeColors; dark?: ThemeColors };
	login?: { backgroundImage?: string };
	footer?: { text?: string };
	email?: { senderName?: string; logoUrl?: string };
}

/** Every key present, for form binding. Empty strings mean "not set". */
export interface EditableBranding {
	schemaVersion: 1;
	identity: { appName: string; appTitle: string; brandName: string; assistantName: string };
	logo: { wordmark: { light: string; dark: string }; mark: { light: string; dark: string }; favicon: string };
	colors: { light: Required<ThemeColors>; dark: Required<ThemeColors> };
	login: { backgroundImage: string };
	footer: { text: string };
	email: { senderName: string; logoUrl: string };
}

export interface BrandingWarning {
	field: string;
	type: string;
	ratio: number;
	minimum: number;
	message: string;
}

export interface TenantBrandingResponse {
	id: string;
	tenantId: string;
	tenantName?: string | null;
	tenantCode?: string | null;
	status: BrandingStatus;
	version: number | null;
	description?: string | null;
	branding: BrandingConfig;
	publishedAt?: string | null;
	publishedBy?: string | null;
	publishedByName?: string | null;
	sourceVersion?: number | null;
	warnings?: BrandingWarning[];
	createdAt?: string;
	updatedAt?: string;
	createdBy?: string | null;
	updatedBy?: string | null;
}

export interface TenantBrandingMeResponse {
	tenantId: string;
	version: number;
	branding: BrandingConfig;
}

export interface BrandingSwatches {
	lightPrimary?: string | null;
	lightAccent?: string | null;
	lightSidebar?: string | null;
	darkPrimary?: string | null;
}

export interface TenantBrandingSummary {
	tenantId: string;
	tenantName?: string | null;
	tenantCode?: string | null;
	state: BrandingSummaryState;
	activeVersion?: number | null;
	hasDraft: boolean;
	appName?: string | null;
	swatches?: BrandingSwatches | null;
	lastChangedAt?: string | null;
	lastChangedByName?: string | null;
}

export interface BrandingCounts {
	tenants: number;
	published: number;
	draftPending: number;
	platformDefault: number;
}

export interface PagedBrandingResponse<T> {
	content?: T[];
	pageNumber?: number;
	pageSize?: number;
	totalElements?: number;
	totalPages?: number;
}

// ── Platform defaults (today's values) ─────────────────────────────────────

/** Built-in look. Empty logo slots mean "keep the bundled ARMA asset". */
export const DEFAULT_BRANDING: EditableBranding = {
	schemaVersion: 1,
	identity: {
		appName: APP.NAME,
		appTitle: 'ARMA - AI-Native Regulatory & Supervisory Platform',
		brandName: 'ARMA',
		assistantName: '',
	},
	logo: { wordmark: { light: '', dark: '' }, mark: { light: '', dark: '' }, favicon: '' },
	colors: {
		light: { primary: '#000000', accent: '#0F172A', accentHover: '#1E293B', sidebar: '#FBFBFB', sidebarBorder: '#E4E4E7', sidebarActive: '#000000' },
		dark: { primary: '#FAFAFA', accent: '#FAFAFA', accentHover: '#E4E4E7', sidebar: '#18181B', sidebarBorder: '#3F3F46', sidebarActive: '#FAFAFA' },
	},
	login: { backgroundImage: '' },
	footer: { text: '© ARMA' },
	email: { senderName: '', logoUrl: '' },
};

export const THEME_COLOR_KEYS = ['primary', 'accent', 'accentHover'] as const;
export const SIDEBAR_COLOR_KEYS = ['sidebar', 'sidebarBorder', 'sidebarActive'] as const;
export type ThemeColorKey = (typeof THEME_COLOR_KEYS)[number];
export type SidebarColorKey = (typeof SIDEBAR_COLOR_KEYS)[number];

// ── Presets (UI-only; the API stores hex values, never a preset id) ────────

export type ThemeColorSet = Record<ThemeColorKey, string>;
export type SidebarColorSet = Record<SidebarColorKey, string>;

export interface ColorPreset {
	id: string;
	name: string;
	light: ThemeColorSet;
	dark: ThemeColorSet;
}

export interface SidebarPreset {
	id: string;
	name: string;
	light: SidebarColorSet;
	dark: SidebarColorSet;
}

export const COLOR_PRESETS: readonly ColorPreset[] = [
	{ id: 'arma', name: 'ARMA default', light: { primary: '#000000', accent: '#0F172A', accentHover: '#1E293B' }, dark: { primary: '#FAFAFA', accent: '#FAFAFA', accentHover: '#E4E4E7' } },
	{ id: 'ocean', name: 'Ocean', light: { primary: '#1A73E8', accent: '#0B3D91', accentHover: '#0F4FB8' }, dark: { primary: '#8AB4F8', accent: '#D2E3FC', accentHover: '#AECBFA' } },
	{ id: 'indigo', name: 'Indigo', light: { primary: '#4F46E5', accent: '#312E81', accentHover: '#3730A3' }, dark: { primary: '#A5B4FC', accent: '#E0E7FF', accentHover: '#C7D2FE' } },
	{ id: 'emerald', name: 'Emerald', light: { primary: '#047857', accent: '#064E3B', accentHover: '#065F46' }, dark: { primary: '#34D399', accent: '#D1FAE5', accentHover: '#A7F3D0' } },
	{ id: 'teal', name: 'Teal', light: { primary: '#0F766E', accent: '#134E4A', accentHover: '#115E59' }, dark: { primary: '#5EEAD4', accent: '#CCFBF1', accentHover: '#99F6E4' } },
	{ id: 'crimson', name: 'Crimson', light: { primary: '#B91C1C', accent: '#7F1D1D', accentHover: '#991B1B' }, dark: { primary: '#FCA5A5', accent: '#FEE2E2', accentHover: '#FECACA' } },
	{ id: 'amber', name: 'Amber', light: { primary: '#B45309', accent: '#78350F', accentHover: '#92400E' }, dark: { primary: '#FCD34D', accent: '#FEF3C7', accentHover: '#FDE68A' } },
	{ id: 'graphite', name: 'Graphite', light: { primary: '#374151', accent: '#111827', accentHover: '#1F2937' }, dark: { primary: '#D1D5DB', accent: '#F3F4F6', accentHover: '#E5E7EB' } },
];

export const SIDEBAR_PRESETS: readonly SidebarPreset[] = [
	{ id: 'arma', name: 'ARMA default', light: { sidebar: '#FBFBFB', sidebarBorder: '#E4E4E7', sidebarActive: '#000000' }, dark: { sidebar: '#18181B', sidebarBorder: '#3F3F46', sidebarActive: '#FAFAFA' } },
	{ id: 'paper', name: 'Paper', light: { sidebar: '#FFFFFF', sidebarBorder: '#E4E4E7', sidebarActive: '#0F172A' }, dark: { sidebar: '#09090B', sidebarBorder: '#27272A', sidebarActive: '#FAFAFA' } },
	{ id: 'mist', name: 'Mist', light: { sidebar: '#F1F5F9', sidebarBorder: '#E2E8F0', sidebarActive: '#1E293B' }, dark: { sidebar: '#0F172A', sidebarBorder: '#1E293B', sidebarActive: '#E2E8F0' } },
	{ id: 'ocean', name: 'Ocean tint', light: { sidebar: '#EFF6FF', sidebarBorder: '#DBEAFE', sidebarActive: '#1D4ED8' }, dark: { sidebar: '#0B1220', sidebarBorder: '#1E3A8A', sidebarActive: '#93C5FD' } },
	{ id: 'forest', name: 'Forest tint', light: { sidebar: '#ECFDF5', sidebarBorder: '#D1FAE5', sidebarActive: '#047857' }, dark: { sidebar: '#06140F', sidebarBorder: '#064E3B', sidebarActive: '#6EE7B7' } },
	{ id: 'midnight', name: 'Midnight', light: { sidebar: '#0F172A', sidebarBorder: '#1E293B', sidebarActive: '#F8FAFC' }, dark: { sidebar: '#020617', sidebarBorder: '#1E293B', sidebarActive: '#E2E8F0' } },
	{ id: 'navy', name: 'Navy', light: { sidebar: '#0B2545', sidebarBorder: '#1B3A63', sidebarActive: '#FFFFFF' }, dark: { sidebar: '#06142A', sidebarBorder: '#12284A', sidebarActive: '#DBEAFE' } },
	{ id: 'plum', name: 'Plum', light: { sidebar: '#2E1065', sidebarBorder: '#4C1D95', sidebarActive: '#F5F3FF' }, dark: { sidebar: '#1A0B3B', sidebarBorder: '#3B1D72', sidebarActive: '#DDD6FE' } },
];

const sameColor = (a: string | undefined, b: string | undefined): boolean =>
	String(a ?? '').trim().toUpperCase() === String(b ?? '').trim().toUpperCase();

function matchPreset<K extends string, P extends { light: Record<K, string>; dark: Record<K, string> }>(
	presets: readonly P[],
	keys: readonly K[],
	colors: { light: Partial<Record<K, string>>; dark: Partial<Record<K, string>> },
): P | null {
	return presets.find(preset =>
		(['light', 'dark'] as const).every(theme => keys.every(key => sameColor(colors[theme][key], preset[theme][key]))),
	) ?? null;
}

export function matchColorPreset(colors: EditableBranding['colors']): ColorPreset | null {
	return matchPreset(COLOR_PRESETS, THEME_COLOR_KEYS, colors);
}

export function matchSidebarPreset(colors: EditableBranding['colors']): SidebarPreset | null {
	return matchPreset(SIDEBAR_PRESETS, SIDEBAR_COLOR_KEYS, colors);
}

// ── Editor document ⇄ API document ─────────────────────────────────────────

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** API branding (any subset) → full editable form, with defaults where a value is missing. */
export function toEditableBranding(config?: BrandingConfig | null): EditableBranding {
	const c = config ?? { schemaVersion: 1 };
	const d = DEFAULT_BRANDING;
	const colors = (theme: BrandingTheme): Required<ThemeColors> => {
		const source = c.colors?.[theme] ?? {};
		const fallback = d.colors[theme];
		return {
			primary: text(source.primary) || fallback.primary,
			accent: text(source.accent) || fallback.accent,
			accentHover: text(source.accentHover) || fallback.accentHover,
			sidebar: text(source.sidebar) || fallback.sidebar,
			sidebarBorder: text(source.sidebarBorder) || fallback.sidebarBorder,
			sidebarActive: text(source.sidebarActive) || fallback.sidebarActive,
		};
	};
	return {
		schemaVersion: 1,
		identity: {
			appName: text(c.identity?.appName) || d.identity.appName,
			appTitle: text(c.identity?.appTitle),
			brandName: text(c.identity?.brandName) || d.identity.brandName,
			assistantName: text(c.identity?.assistantName),
		},
		logo: {
			wordmark: { light: text(c.logo?.wordmark?.light), dark: text(c.logo?.wordmark?.dark) },
			mark: { light: text(c.logo?.mark?.light), dark: text(c.logo?.mark?.dark) },
			favicon: text(c.logo?.favicon),
		},
		colors: { light: colors('light'), dark: colors('dark') },
		login: { backgroundImage: text(c.login?.backgroundImage) },
		footer: { text: text(c.footer?.text) },
		email: { senderName: text(c.email?.senderName), logoUrl: text(c.email?.logoUrl) },
	};
}

/**
 * Form → API document. Drops blank values (the API rejects empty strings) and empty groups,
 * trims text, and upper-cases hex colors.
 */
export function compactBranding(form: EditableBranding): BrandingConfig {
	const trimObject = <T extends Record<string, string>>(source: T, hex = false): Partial<T> | undefined => {
		const out: Partial<T> = {};
		for (const [key, value] of Object.entries(source) as [keyof T, string][]) {
			const trimmed = String(value ?? '').trim();
			if (trimmed) out[key] = (hex ? trimmed.toUpperCase() : trimmed) as T[keyof T];
		}
		return Object.keys(out).length ? out : undefined;
	};
	const doc: BrandingConfig = { schemaVersion: 1 };
	const identity = trimObject(form.identity);
	if (identity) doc.identity = identity;
	const wordmark = trimObject(form.logo.wordmark);
	const mark = trimObject(form.logo.mark);
	const favicon = form.logo.favicon.trim();
	if (wordmark || mark || favicon) {
		doc.logo = { ...(wordmark ? { wordmark } : {}), ...(mark ? { mark } : {}), ...(favicon ? { favicon } : {}) };
	}
	const light = trimObject(form.colors.light, true);
	const dark = trimObject(form.colors.dark, true);
	if (light || dark) doc.colors = { ...(light ? { light } : {}), ...(dark ? { dark } : {}) };
	const login = trimObject(form.login);
	if (login) doc.login = login;
	const footer = trimObject(form.footer);
	if (footer) doc.footer = footer;
	const email = trimObject(form.email);
	if (email) doc.email = email;
	return doc;
}

// ── Runtime resolution ─────────────────────────────────────────────────────

export interface ResolvedBranding {
	appName: string;
	appTitle: string;
	brandName: string;
	footerText: string;
	favicon: string;
	wordmark: { light: string; dark: string };
	mark: { light: string; dark: string };
}

/** Field-by-field fallback to {@link DEFAULT_BRANDING}. Empty logo slots stay empty (bundled asset). */
export function resolveBranding(config?: BrandingConfig | null): ResolvedBranding {
	const e = toEditableBranding(config);
	return {
		appName: e.identity.appName,
		appTitle: e.identity.appTitle || DEFAULT_BRANDING.identity.appTitle,
		brandName: e.identity.brandName,
		footerText: e.footer.text || DEFAULT_BRANDING.footer.text,
		favicon: e.logo.favicon,
		wordmark: { ...e.logo.wordmark },
		mark: { ...e.logo.mark },
	};
}

// ── Validation (same rules as BrandingValidator) ───────────────────────────

export type BrandingErrors = Record<string, string>;

const HEX = /^#[0-9A-Fa-f]{6}$/;
const IMAGE_EXT = ['.png', '.svg', '.jpg', '.jpeg', '.webp'];
const FAVICON_EXT = [...IMAGE_EXT, '.ico'];
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/;

/** Public API path prefix for tenant branding images served from S3 via the API. */
export const PLATFORM_BRANDING_ASSET_PATH = '/api/v1/public/branding-assets/';

function endsWithImageExt(path: string, favicon: boolean): boolean {
	const lower = path.toLowerCase();
	return (favicon ? FAVICON_EXT : IMAGE_EXT).some(ext => lower.endsWith(ext));
}

export function isPlatformBrandingAssetUrl(value: string): boolean {
	const trimmed = value.trim();
	if (trimmed.startsWith(PLATFORM_BRANDING_ASSET_PATH)) return true;
	try {
		return new URL(trimmed).pathname.includes(PLATFORM_BRANDING_ASSET_PATH);
	} catch {
		return false;
	}
}

/**
 * Resolve a stored logo URL for browser use. Relative platform paths (and older
 * absolute localhost platform URLs) are rewritten to the current API origin.
 */
export function resolveBrandingAssetUrl(value: string | null | undefined, apiBaseUrl: string): string {
	const raw = (value ?? '').trim();
	if (!raw) return '';
	const base = apiBaseUrl.replace(/\/$/, '');
	if (raw.startsWith(PLATFORM_BRANDING_ASSET_PATH)) {
		return `${base}${raw}`;
	}
	try {
		if (raw.startsWith('http://') || raw.startsWith('https://')) {
			const url = new URL(raw);
			const idx = url.pathname.indexOf(PLATFORM_BRANDING_ASSET_PATH);
			if (idx >= 0) {
				return `${base}${url.pathname.slice(idx)}${url.search}`;
			}
		}
	} catch {
		/* keep raw */
	}
	return raw;
}

function isAllowedImageUrl(value: string, favicon: boolean): boolean {
	const lower = value.toLowerCase();
	if (value.length > 2048 || lower.startsWith('data:') || lower.startsWith('javascript:')) {
		return false;
	}
	if (value.startsWith(PLATFORM_BRANDING_ASSET_PATH)) {
		return endsWithImageExt(value, favicon);
	}
	try {
		const url = new URL(value);
		if (!url.hostname) return false;
		const https = url.protocol === 'https:';
		const platformHttp = url.protocol === 'http:' && isPlatformBrandingAssetUrl(value);
		if (!https && !platformHttp) return false;
		return endsWithImageExt(url.pathname, favicon);
	} catch {
		return false;
	}
}

/** Inline errors keyed by dotted path. `forPublish` adds the publish-only rules. */
export function validateBranding(form: EditableBranding, forPublish: boolean): BrandingErrors {
	const doc = compactBranding(form);
	const errors: BrandingErrors = {};
	const textRule = (path: string, value: string | undefined, max: number, required: boolean, controls = false) => {
		if (value === undefined) {
			if (required) errors[path] = 'Required to publish.';
			return;
		}
		if (value.length > max) errors[path] = `Must be 1–${max} characters.`;
		else if (controls && CONTROL_CHARS.test(value)) errors[path] = 'Must not contain control characters.';
	};
	textRule('identity.appName', doc.identity?.appName, 60, forPublish, true);
	textRule('identity.appTitle', doc.identity?.appTitle, 120, false);
	textRule('identity.brandName', doc.identity?.brandName, 40, forPublish);
	textRule('identity.assistantName', doc.identity?.assistantName, 40, false);

	const urlRule = (path: string, value: string | undefined, favicon = false) => {
		if (value !== undefined && !isAllowedImageUrl(value, favicon)) {
			errors[path] = favicon
				? 'Must be an https URL to a .ico, .png, .svg, .jpg, .jpeg, or .webp file.'
				: 'Must be an https URL ending in .png, .svg, .jpg, .jpeg, or .webp.';
		}
	};
	urlRule('logo.wordmark.light', doc.logo?.wordmark?.light);
	urlRule('logo.wordmark.dark', doc.logo?.wordmark?.dark);
	urlRule('logo.mark.light', doc.logo?.mark?.light);
	urlRule('logo.mark.dark', doc.logo?.mark?.dark);
	urlRule('logo.favicon', doc.logo?.favicon, true);
	urlRule('login.backgroundImage', doc.login?.backgroundImage);
	urlRule('email.logoUrl', doc.email?.logoUrl);

	for (const theme of ['light', 'dark'] as const) {
		const colors = doc.colors?.[theme] ?? {};
		for (const key of [...THEME_COLOR_KEYS, ...SIDEBAR_COLOR_KEYS]) {
			const value = colors[key];
			if (value !== undefined && !HEX.test(value)) errors[`colors.${theme}.${key}`] = 'Must be a hex color like #1A73E8.';
		}
		if (forPublish) {
			const present = SIDEBAR_COLOR_KEYS.filter(key => colors[key] !== undefined);
			if (present.length && present.length < SIDEBAR_COLOR_KEYS.length) {
				for (const key of SIDEBAR_COLOR_KEYS) {
					if (colors[key] === undefined) {
						errors[`colors.${theme}.${key}`] = 'sidebar, sidebarBorder, and sidebarActive must all be set together.';
					}
				}
			}
		}
	}

	const footer = doc.footer?.text;
	if (footer !== undefined) {
		if (footer.length > 120) errors['footer.text'] = 'Must be 120 characters or fewer.';
		else if (/[<>]/.test(footer)) errors['footer.text'] = 'Must be plain text.';
	}
	const sender = doc.email?.senderName;
	if (sender !== undefined && (sender.length > 60 || /[<>"\r\n]/.test(sender))) {
		errors['email.senderName'] = 'Must be 60 characters or fewer and must not contain <, >, ", or line breaks.';
	}
	return errors;
}

// ── Contrast ───────────────────────────────────────────────────────────────

function luminance(color: string): number | null {
	const rgb = parseCssColor(color);
	if (!rgb) return null;
	const channel = (c: number) => {
		const v = c / 255;
		return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
	};
	return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** WCAG contrast ratio between two hex / rgb colors (1–21). `null` when either is unreadable. */
export function contrastRatio(foreground: string, background: string): number | null {
	const a = luminance(foreground);
	const b = luminance(background);
	if (a === null || b === null) return null;
	const [hi, lo] = a > b ? [a, b] : [b, a];
	return (hi + 0.05) / (lo + 0.05);
}

export function meetsAA(foreground: string, background: string, minimum = 4.5): boolean {
	const ratio = contrastRatio(foreground, background);
	return ratio !== null && ratio >= minimum;
}

/** Text color the app derives for a surface: `#ffffff` or `#0f172a` (same as `_contrast.scss`). */
export function readableOn(background: string): string {
	return pickContrastingForeground(background);
}

/** Blend `a` over `b` (`weight` of a). Both `#RRGGBB`. */
export function mixColors(a: string, b: string, weight: number): string {
	const ca = parseCssColor(a);
	const cb = parseCssColor(b);
	if (!ca || !cb) return a;
	const mix = (x: number, y: number) => Math.round(x * weight + y * (1 - weight)).toString(16).padStart(2, '0');
	return `#${mix(ca.r, cb.r)}${mix(ca.g, cb.g)}${mix(ca.b, cb.b)}`.toUpperCase();
}

/** Muted nav text: the readable color blended toward the background, never below 4.5:1. */
export function mutedTextOn(background: string): string {
	const foreground = readableOn(background);
	for (let weight = 0.62; weight < 1; weight += 0.02) {
		const candidate = mixColors(foreground, background, weight);
		if (meetsAA(candidate, background)) return candidate;
	}
	return foreground;
}

// ── CSS tokens ─────────────────────────────────────────────────────────────

/** `--primary` / `--accent-surface*` values that differ from the theme's defaults. */
export function deriveColorTokens(theme: BrandingTheme, colors?: ThemeColors | null): Record<string, string> {
	const out: Record<string, string> = {};
	const defaults = DEFAULT_BRANDING.colors[theme];
	const map: Record<ThemeColorKey, string> = { primary: '--primary', accent: '--accent-surface', accentHover: '--accent-surface-hover' };
	for (const key of THEME_COLOR_KEYS) {
		const value = colors?.[key];
		if (value && HEX.test(value) && !sameColor(value, defaults[key])) out[map[key]] = value.toUpperCase();
	}
	return out;
}

/**
 * Sidebar tokens for one theme, or `null` when the theme's three sidebar values are missing,
 * invalid, or equal to the defaults (so today's exact nav colors stay in place).
 */
export function deriveSidebarTokens(theme: BrandingTheme, colors?: ThemeColors | null): Record<string, string> | null {
	const background = colors?.sidebar;
	const border = colors?.sidebarBorder;
	const active = colors?.sidebarActive;
	if (!background || !border || !active || ![background, border, active].every(value => HEX.test(value))) return null;
	const defaults = DEFAULT_BRANDING.colors[theme];
	if (sameColor(background, defaults.sidebar) && sameColor(border, defaults.sidebarBorder) && sameColor(active, defaults.sidebarActive)) {
		return null;
	}
	const foreground = readableOn(background);
	return {
		'--sidebar': background.toUpperCase(),
		'--sidebar-foot-bg': background.toUpperCase(),
		'--sidebar-border': border.toUpperCase(),
		'--sidebar-edge': border.toUpperCase(),
		'--sidebar-border-soft': border.toUpperCase(),
		'--sidebar-fg': foreground,
		'--sidebar-muted-fg': mutedTextOn(background),
		'--sidebar-hover': mixColors(foreground, background, 0.1),
		'--sidebar-active': active.toUpperCase(),
		'--sidebar-active-fg': readableOn(active),
	};
}

/** All custom properties branding sets for one theme (empty when it matches the defaults). */
export function themeTokens(theme: BrandingTheme, config?: BrandingConfig | null): Record<string, string> {
	const colors = config?.colors?.[theme];
	return { ...deriveColorTokens(theme, colors), ...(deriveSidebarTokens(theme, colors) ?? {}) };
}

const declarations = (tokens: Record<string, string>): string =>
	Object.entries(tokens).map(([name, value]) => `${name}: ${value};`).join(' ');

/** CSS string for a cssText-safe `url()` value. */
function cssUrl(value: string): string {
	return `url("${value.replace(/["\\\n\r]/g, encodeURIComponent)}")`;
}

/**
 * Stylesheet for published branding. Empty string when nothing differs from today.
 * Light rules use `:root:not([data-theme='dark'])` so they never leak into dark mode.
 */
export function buildBrandingCss(config?: BrandingConfig | null): string {
	if (!config) return '';
	const blocks: string[] = [];
	const light = themeTokens('light', config);
	const dark = themeTokens('dark', config);
	if (Object.keys(light).length) blocks.push(`:root:not([data-theme='dark']) { ${declarations(light)} }`);
	if (Object.keys(dark).length) blocks.push(`:root[data-theme='dark'] { ${declarations(dark)} }`);
	const background = config.login?.backgroundImage;
	if (background && isAllowedImageUrl(background, false)) {
		blocks.push(`:root { --auth-bg-image: ${cssUrl(background)}; }`);
		blocks.push('.auth-page-bg { background-size: cover; background-position: center; }');
	}
	return blocks.join('\n');
}
